<template>
  <div>
    <gg-loading v-if="loading" :loading="true" type="card" />

    <gg-card v-else-if="!service">
      <gg-card-text>
        <div class="py-8 text-center">
          <p class="mb-2 font-weight-medium">We couldn't find this email account.</p>
          <p class="mb-4">It may have been cancelled, or the address is wrong.</p>
          <gg-btn secondary to="/email">Back to Email</gg-btn>
        </div>
      </gg-card-text>
    </gg-card>

    <gg-card v-else-if="!hostingReady">
      <gg-card-text>
        <div class="py-8 text-center">
          <p class="mb-4 font-weight-medium">We couldn't open the hosting service behind this account.</p>
          <gg-btn secondary @click="load">Try again</gg-btn>
        </div>
      </gg-card-text>
    </gg-card>

    <template v-else>
      <v-row class="align-center mb-3">
        <v-col cols="12" class="d-flex align-center flex-wrap">
          <div class="mr-auto my-1">
            <!-- Built here rather than through the global $breadcrumbs
                 plugin: src/plugins/breadcrumbs.js writes the substituted path
                 back onto the shared vue-router record, so once one account
                 has been opened the crumb keeps its address for every account
                 after it. `gg-breadcrumbs` is the app's existing breadcrumb
                 styling, dark theme included. -->
            <v-breadcrumbs class="pa-0 pb-1 gg-breadcrumbs" :items="crumbs">
              <template v-slot:divider>
                <v-icon small>mdi-chevron-right</v-icon>
              </template>
            </v-breadcrumbs>

            <gg-card-title class="pt-0 pb-0 mb-3">{{ isAddon ? mainDomain : service.domain }}</gg-card-title>

            <div class="body-2">
              <span class="text--secondary">Email Plan:</span> {{ account && account.planLabel }}
            </div>
            <div v-if="isAddon" class="body-2 mt-1">
              <span class="text--secondary">Addon domain of:</span> {{ service.domain }}
            </div>
            <div v-else-if="addonDomains.length" class="body-2 mt-1">
              <span class="text--secondary">Addon domains:</span> {{ addonDomains.join(', ') }}
            </div>
          </div>
        </v-col>
      </v-row>

      <v-tabs flat background-color="transparent" class="email-account-tabs mb-0">
        <v-tabs-slider color="green"></v-tabs-slider>
        <v-tab v-for="section in sections" :key="section.path" class="text-none" :to="sectionPath(section)">
          {{ section.title }}
        </v-tab>
      </v-tabs>
      <hr class="mt-0 mb-4">

      <!-- The Mailboxes tab emits this from its Add Mailbox button (AM-1109)
           and its empty state, as do other tabs whose empty state offers
           "Create mailbox". -->
      <router-view :service="service" @create-mailbox="modals.create = true" />

      <!-- Creating re-reads the service itself, so there is nothing to do on
           `created` beyond closing. -->
      <create-mailbox-modal
        :is-active="modals.create"
        :service-id="service.id"
        :default-domain="isAddon ? mainDomain : ''"
        @close="modals.create = false"
      />
    </template>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import CreateMailboxModal from '@/components/Email/CreateMailboxModal'
import { accountPath } from '@/consts/email'

/**
 * Shell for one email account, addressed by its main domain.
 *
 * Child routes reuse components from the hosting Email section, and those call
 * `amapi.cpanel_uapi` with no `id`, which then falls back to
 * `store.getters.hosting`. So nothing below this shell renders until that
 * getter points at this account's service — otherwise they would read and
 * change whichever service was opened last.
 */
const SECTIONS = [
  { title: 'Mailboxes', path: 'accounts' },
  // Aliases, Security and Client Setup work on one mailbox at a time
  // (AccountMailboxSectionView).
  { title: 'Aliases', path: 'aliases', perMailbox: true },
  { title: 'Forwarders', path: 'forwarders' },
  { title: 'Auto Reply', path: 'autoresponders' },
  { title: 'Filters', path: 'filters' },
  { title: 'Spam', path: 'spam' },
  { title: 'Security', path: 'security', perMailbox: true },
  { title: 'Client Setup', path: 'client-setup', perMailbox: true },
  // DKIM and SPF of the domain the account is opened on (AM-1108).
  { title: 'Domain Settings', path: 'domain-settings' },
]

export default {
  name: 'EmailAccountView',
  components: {
    CreateMailboxModal,
  },
  data () {
    return {
      service: null,
      hostingReady: false,
      loading: true,
      sections: SECTIONS,
      modals: {
        create: false,
      },
    }
  },
  computed: {
    ...mapGetters('emailHub', ['emailAccountFor']),
    // vue-router has already decoded this. The main domain, or an addon
    // domain opened as its own account — see `isAddon`.
    mainDomain () {
      return this.$route.params.maindomain
    },
    account () {
      return this.service ? this.emailAccountFor(this.service.domain) : null
    },
    addonDomains () {
      return this.account ? this.account.addonDomains : []
    },
    // Opened through one of the service's addon domains, which is managed as
    // an account of its own (AM-1104, 2026-10-07).
    isAddon () {
      return !!this.service &&
        String(this.service.domain).toLowerCase() !== String(this.mainDomain).toLowerCase()
    },
    crumbs () {
      return [
        { text: 'Email', to: '/email', exact: true },
        { text: this.mainDomain, disabled: true },
      ]
    },
  },
  methods: {
    ...mapActions('emailHub', [
      'resolveServiceByDomain',
      'ensureHostingContext',
      'loadDomainsForService',
    ]),
    ...mapActions(['reportError']),
    /**
     * The hosting Email modules keep one service's data, and never reset it when
     * the service changes — `email` even remembers the chosen domain and sends
     * it with the next request. Left alone, the tabs below would query this
     * account's cPanel with the previous account's domain.
     */
    clearServiceStores () {
      this.$store.commit('email/CLEAR_EMAIL_STORE')
      this.$store.commit('spam_filters/CLEAR_SPAM_FILTERS_STORE')
      this.$store.commit('email_deliverability/CLEAR_STORE')
    },
    /**
     * Moving between the per-mailbox tabs keeps the mailbox that is open, so
     * checking one mailbox's aliases and then its security is a single click.
     */
    sectionPath (section) {
      const mailbox = section.perMailbox ? this.$route.params.mailbox : ''
      return accountPath(this.mainDomain, section.path, mailbox)
    },
    async load () {
      this.loading = true
      this.hostingReady = false
      try {
        this.service = await this.resolveServiceByDomain(this.mainDomain)
        if (!this.service) return

        const previous = this.$store.getters.hosting && this.$store.getters.hosting.id
        this.hostingReady = await this.ensureHostingContext(this.service.id)
        if (this.hostingReady && previous !== this.service.id) {
          this.clearServiceStores()
        }
      } catch (e) {
        this.service = null
        this.reportError('Unable to load this email account.')
      } finally {
        this.loading = false
      }

      // Addon domains are header detail, so they load after the page is up
      // and a failure leaves the rest of it working.
      if (this.service) {
        this.loadDomainsForService(this.service.id).catch(e => console.warn(e))
      }
    },
  },
  watch: {
    mainDomain () {
      this.load()
    },
  },
  created () {
    this.load()
  },
}
</script>

<style lang="scss" scoped>
// Once the tabs overflow, v-slide-group reserves room for its scroll arrows
// even when there is nothing to scroll back to, which pushes the first tab
// off the left edge. Keep the left arrow only while it can be used.
.email-account-tabs ::v-deep .v-slide-group__prev--disabled {
  display: none;
}
</style>
